import { ContactUsLink } from '@components/ContactUsLink/ContactUsLink';
import { Toast, ToastAction } from '@components/Toast/Toast';
import { describeMediaCommentError } from '@lib/media/mediaCommentErrors';
import type { OriginalUnavailableReason } from '@lib/media/originalMedia';
import { describeLibraryFolderError } from '@lib/network/libraryFolders';
import { ACCEPTED_IMAGE_SEARCH_EXTENSIONS } from '@lib/utils/fileExtensions';
import { visualSearchModesPhrase } from '@lib/utils/searchTypeOptions';
import { describeSocialConnectError } from '@lib/utils/socialConnectReturn';
import { formatClockLabel, pluralize } from '@lib/utils/units';
import { debugStore } from '@stores/Debug';
import { NotifBellEntryDetails, notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { OVER_DURATION_LIMIT_CONTACT_MESSAGE } from '@stores/UploadURL/mediaStatus';
import { Media } from '@typings/Media';
import React from 'react';
import { toast } from 'sonner';

// Toast template definitions: success and error for now
type ToastVariant = 'success' | 'error';

/** Where a template's payload is allowed to render.
 * - `'toast'`: the visual sonner toast that appears near the screen edge.
 * - `'upload-activity'`: a generic-toast entry mirrored into the upload
 *   activity panel (the icon next to the user menu, formerly called "the
 *   bell"). Suppress for templates already covered by a BatchCard there
 *   (e.g. upload start / completion).
 * - `'notif-bell'`: a notif-bell entry mirrored into the new general-
 *   purpose notifications bell. Use this for non-upload activity (media
 *   deletes, downloads, library settings, moment exports, etc.) that
 *   doesn't have an upload-panel home. Distinct token from the legacy
 *   `'bell'` so it's unambiguous which surface a template targets. */
export type ToastSurface = 'toast' | 'upload-activity' | 'notif-bell';

const DEFAULT_SURFACES: ToastSurface[] = ['toast', 'upload-activity'];

interface ToastTemplate {
  header: string | ((data: any) => string | React.ReactNode);
  description?: string | ((data: any) => string | React.ReactNode);
  variant: ToastVariant | ((data: any) => ToastVariant);
  duration?: number;
  /** Optional stable id so this toast is distinct from others (e.g. avoids merging with in‑progress toasts). */
  id?: string | ((data: any) => string);
  /** If true, suppress duplicate toasts while the previous one is still within its visible duration. */
  suppressIfRecent?: boolean;
  /** A button on the visual toast, such as "Undo". The bell mirrors leave it out. */
  action?: (data: any) => ToastAction | undefined;
  /** Custom render function. When set, renders this instead of the default Toast component. */
  customRender?: (toastId: string | number, data: any) => React.ReactElement;
  /** Where this template renders. Defaults to `['toast', 'upload-activity']`.
   * Drop `'upload-activity'` for templates whose information is already
   * represented by a BatchCard in the upload panel (keeps the ephemeral
   * toast, suppresses the redundant panel card). Add `'notif-bell'` for
   * non-upload activity that should land in the new general-purpose bell. */
  surfaces?: ToastSurface[];
  /** Optional structured payload attached to the notif-bell mirror. The
   * bell card uses this to render a "More info" affordance (e.g. a modal
   * listing affected filenames for bulk operations). The ephemeral toast
   * never uses it — only the bell mirror does. */
  extractDetails?: (data: any) => NotifBellEntryDetails | undefined;
}

const DEFAULT_TOAST_DURATION = 10 * 1000; // 10 seconds

/** Which comment write failed — the header names the action the reader pressed. */
export type MediaCommentAction =
  | 'post'
  | 'edit'
  | 'delete'
  | 'reply'
  | 'edit-reply'
  | 'delete-reply';

const MEDIA_COMMENT_ACTION_HEADERS: Record<MediaCommentAction, string> = {
  post: "Couldn't post your comment",
  edit: "Couldn't save your edit",
  delete: "Couldn't delete that comment",
  reply: "Couldn't post your reply",
  'edit-reply': "Couldn't save your edit",
  'delete-reply': "Couldn't delete that reply",
};

/** Which folder write failed — the header names what the user just did. */
export type FolderAction = 'create' | 'edit' | 'move' | 'delete' | 'move-media' | 'undo';

const FOLDER_ACTION_FAILED_HEADERS: Record<FolderAction, string> = {
  create: "Couldn't create the folder",
  edit: "Couldn't save the folder",
  move: "Couldn't move the folder",
  delete: "Couldn't delete the folder",
  'move-media': "Couldn't move the media",
  undo: "Couldn't undo the move",
};

// Track recent toast template IDs for suppression (templateId → timestamp)
const recentToasts = new Map<string, number>();

// Store active toast IDs for toasts with suppressIfRecent, so we can re-trigger their animation
const activeToastIds = new Map<string, string | number>();

// Toast templates dictionary (grouped by category for easier browsing).
// When adding a new toast: pick the section that matches its domain and add it there.
// TODO: Update some messages to be more specific (e.g. include filenames, counts, or context where helpful).

type SocialConnectSuccessData = {
  platformLabel: string;
  /** "account", or "Page" on Facebook, from the platform's module. */
  accountNoun: string;
  /** How many accounts the login connected. */
  count: number;
  /** The names the connections list holds for them; fewer than `count` when the refetch failed. */
  names: string[];
};

type SocialConnectFailedData = {
  platformLabel: string;
  errorCode: string;
  /** The platform module's wording of `no_accounts`, null for a platform without a module here. */
  noAccountsNote: string | null;
};

/** "A, B and C", as the UI's locale writes a list. */
const listNames = (names: string[]): string =>
  new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(names);

const toastTemplates: Record<string, ToastTemplate> = {
  // --- Upload (file): queued, cleared, batch complete, duplicates ---
  'upload-queued': {
    header: (data: { files: File[] }) => {
      const nFiles = data.files.length;
      if (nFiles === 1) {
        return `"${data.files[0].name}" added to upload queue`;
      }
      return `${nFiles} files added to upload queue`;
    },
    description: "Please don't leave this page.",
    variant: 'success',
    duration: DEFAULT_TOAST_DURATION,
    // Bell already shows the new batch's BatchCard the moment the queue
    // entries are added, so the mirrored "added to upload queue" card would
    // just duplicate it. Keep the fleeting toast, drop the bell card.
    surfaces: ['toast'],
  },
  'upload-cleared': {
    header: (data: { filename: string }) => `Upload of ${data.filename} cleared.`,
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  'uploads-all-cleared': {
    header: 'All uploads cleared.',
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  'upload-batch-complete': {
    header: (data: {
      nSuccess: number;
      nFailed: number;
      totalAttempted: number;
      successFilename?: string;
      failedFilename?: string;
    }) => {
      const { nSuccess, nFailed, totalAttempted, successFilename, failedFilename } = data;
      // Single file upload
      if (totalAttempted === 1) {
        if (nSuccess === 1 && successFilename) return 'Upload complete';
        if (nFailed === 1 && failedFilename) return 'Upload failed';
      }
      // All succeeded
      if (nSuccess === totalAttempted) return 'Uploads complete';
      // All failed
      if (nFailed === totalAttempted) return 'Uploads failed';
      // Mixed
      return 'Uploads finished';
    },
    description: (data: {
      nSuccess: number;
      nFailed: number;
      totalAttempted: number;
      hasActiveUploads: boolean;
      successFilename?: string;
      failedFilename?: string;
      stillUploadingCount?: number;
      stillUploadingFirstFilename?: string;
    }) => {
      const {
        nSuccess,
        nFailed,
        totalAttempted,
        hasActiveUploads,
        successFilename,
        failedFilename,
        stillUploadingCount,
        stillUploadingFirstFilename,
      } = data;

      const stillUploadingSuffix = ((): React.ReactNode => {
        if (!hasActiveUploads) return '';
        const pleaseStay = "Please don't leave this tab.";
        if (stillUploadingCount != null && stillUploadingCount > 0 && stillUploadingFirstFilename) {
          if (stillUploadingCount === 1) {
            return (
              <>
                {' '}
                <b>{stillUploadingFirstFilename}</b> is still uploading. {pleaseStay}
              </>
            );
          }
          const others = stillUploadingCount - 1;
          const othersText = others === 1 ? '1 other' : `${others} others`;
          return (
            <>
              {' '}
              <b>{stillUploadingFirstFilename}</b> and {othersText} are still uploading.{' '}
              {pleaseStay}
            </>
          );
        }
        return ` Some files are still uploading. ${pleaseStay}`;
      })();

      const trailingSuffix = hasActiveUploads
        ? stillUploadingSuffix
        : ' You may now close this tab.';

      // Single file upload (success)
      if (nSuccess === 1 && totalAttempted === 1 && successFilename) {
        return (
          <>
            The file <b>{successFilename}</b> was successfully uploaded.{trailingSuffix}
          </>
        );
      }
      // Single file upload (failed)
      if (nFailed === 1 && totalAttempted === 1 && failedFilename) {
        return (
          <>
            The file <b>{failedFilename}</b> failed to upload.{trailingSuffix}
          </>
        );
      }
      // All succeeded (multi-file)
      if (nSuccess === totalAttempted && nSuccess > 1) {
        if (successFilename) {
          const others = nSuccess - 1;
          const othersText = others === 1 ? '1 other' : `${others} others`;
          return (
            <>
              <b>{successFilename}</b> and {othersText} uploaded successfully.{trailingSuffix}
            </>
          );
        }
        return `${nSuccess} files uploaded successfully.${trailingSuffix}`;
      }
      // All failed (multi-file)
      if (nFailed === totalAttempted && nFailed > 1) {
        if (failedFilename) {
          const others = nFailed - 1;
          const othersText = others === 1 ? '1 other' : `${others} others`;
          return (
            <>
              <b>{failedFilename}</b> and {othersText} failed to upload.{trailingSuffix}
            </>
          );
        }
        return `${nFailed} files failed to upload.${trailingSuffix}`;
      }
      // Mixed results
      const successText =
        nSuccess > 0
          ? successFilename
            ? nSuccess === 1
              ? `${successFilename} uploaded`
              : `${successFilename} and ${nSuccess - 1} ${nSuccess === 2 ? 'other' : 'others'} uploaded`
            : `${pluralize(nSuccess, 'file')} uploaded`
          : '';
      const failedText =
        nFailed > 0
          ? failedFilename
            ? nFailed === 1
              ? `${failedFilename} failed`
              : `${failedFilename} and ${nFailed - 1} ${nFailed === 2 ? 'other' : 'others'} failed`
            : `${pluralize(nFailed, 'file')} failed`
          : '';

      // Build message parts: parts[0] = success fragment, parts[1] = failure fragment (if both exist)
      const parts = [];
      if (successText && successFilename) {
        parts.push(
          <>
            <b>{successFilename}</b>
            {nSuccess > 1 && ` and ${nSuccess - 1} ${nSuccess === 2 ? 'other' : 'others'}`} uploaded
          </>,
        );
      } else if (successText) {
        parts.push(successText);
      }
      if (failedText && failedFilename) {
        parts.push(
          <>
            <b>{failedFilename}</b>
            {nFailed > 1 && ` and ${nFailed - 1} ${nFailed === 2 ? 'other' : 'others'}`} failed
          </>,
        );
      } else if (failedText) {
        parts.push(failedText);
      }

      // Combine parts based on what's present
      if (parts.length === 0) {
        return `Batch completed.${trailingSuffix}`;
      }
      if (parts.length === 1) {
        // Only successes or only failures
        return (
          <>
            {parts[0]}.{trailingSuffix}
          </>
        );
      }
      // Both successes and failures (mixed results)
      return (
        <>
          {parts[0]}, {parts[1]}.{trailingSuffix}
        </>
      );
    },
    variant: (data: { nSuccess: number; totalAttempted: number }) =>
      data.nSuccess === data.totalAttempted ? 'success' : 'error',
    duration: DEFAULT_TOAST_DURATION,
    // BatchCards in the bell already convey done-success / done-partial-
    // failure / done-total-failure via their status pill + failure count,
    // so mirroring this toast in the bell produced redundant generic-toast
    // cards (also stripped of the filename context because the description
    // is JSX, not a string). Keep the fleeting toast, drop the bell card.
    surfaces: ['toast'],
  },
  'duplicates-skipped': {
    header: (data: { count: number }) => `${pluralize(data.count, 'duplicate')} skipped`,
    description: (data: { count: number }) =>
      data.count === 1
        ? 'Identical file already exists in this folder.'
        : 'Identical files already exist in this folder.',
    variant: 'success',
    duration: 5 * 1000,
  },

  // --- Add (URL / playlist / channel): adding from YouTube etc. ---
  'playlist-uploading': {
    header: 'Adding playlist...',
    description: (data: { count: number; firstTitle?: string; libraryName: string }) =>
      data.count === 1
        ? `Adding ${data.firstTitle} to ${data.libraryName}.`
        : `Adding all ${data.count} media in this playlist to ${data.libraryName}.`,
    variant: 'success',
  },
  'playlist-done': {
    header: 'Playlist added.',
    variant: 'success',
  },
  'folder-uploading': {
    // "Google Drive folder" (not just "folder") to avoid confusion with the
    // local file-folder drag-and-drop upload.
    header: 'Adding Google Drive folder...',
    description: (data: { count: number; folderName?: string; libraryName: string }) => {
      const from = data.folderName ? ` from “${data.folderName}”` : '';
      return data.count === 1
        ? `Adding 1 item${from} to ${data.libraryName}.`
        : `Adding all ${data.count} items${from} to ${data.libraryName}.`;
    },
    variant: 'success',
  },
  'folder-done': {
    header: 'Google Drive folder added.',
    variant: 'success',
  },
  'media-too-long': {
    header: "Media's too long",
    // Mirrors the picker's 10-hour error wording; "Contact us" opens Intercom.
    description: () => (
      <>
        This media exceeds the 10-hour limit.{' '}
        <ContactUsLink message={OVER_DURATION_LIMIT_CONTACT_MESSAGE} /> if you need a higher limit.
      </>
    ),
    variant: 'error',
  },
  'channel-uploading': {
    header: 'Adding channel.',
    description: (data: { count: number; libraryName: string }) =>
      data.count === 1
        ? `Adding 1 media from this channel to ${data.libraryName}. This may take a while...`
        : `Adding all ${data.count} media in this channel to ${data.libraryName}. This may take a while...`,
    variant: 'success',
  },
  'channel-done': {
    header: 'Channel added.',
    description: (data: { count: number }) =>
      data.count === 1
        ? `We've added 1 media from this channel and we are now indexing it for faster search.`
        : `We've added all ${data.count} media in this channel and we are now indexing them for faster search.`,
    variant: 'success',
  },
  'single-video-uploading': {
    header: 'Adding media...',
    variant: 'success',
  },
  'single-video-done': {
    header: 'Media added.',
    description: `We've added the media and we are now indexing it for faster search.`,
    variant: 'success',
  },

  // --- Upload (add / cancel): adding media to library failed, upload cancelled, drag-drop rejected ---
  'library-add-failed': {
    header: (data: { libraryName?: string }) =>
      data?.libraryName
        ? `Failed to add media to ${data.libraryName}`
        : 'Failed to add media to library',
    description: (data: { message?: string }) => {
      if (data?.message) {
        return data.message;
      }
      return 'An error occurred while adding media to your library. Please try again.';
    },
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    id: (data: { libraryName?: string }) =>
      data?.libraryName ? `library-add-failed-${data.libraryName}` : 'library-add-failed',
  },
  'media-import-cancelled': {
    header: 'Media import cancelled',
    description: 'Some media might have been added before you cancelled.',
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
  },
  'media-upload-cancelled': {
    header: 'Media upload cancelled',
    description: 'Some media might have been uploaded before you cancelled.',
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
  },
  // `drag-drop-all-rejected` and `drag-drop-all-duplicates` were removed —
  // both are now surfaced as persistent rich-event cards in the upload
  // activity panel rather than fleeting toasts (see useDragDropUpload.ts
  // and useFileUploadFlow.ts).

  // --- Media (delete): single or bulk media deletion success/error ---
  'media-delete-success-single': {
    header: (data: { filename: string }) => `${data.filename} deleted.`,
    description: (data: { filename: string; libraryName: string }) =>
      `${data.filename} deleted from the library ${data.libraryName}.`,
    variant: 'success',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast', 'notif-bell'],
  },
  'media-delete-error-single': {
    header: (data: { filename: string }) => `Failed to delete ${data.filename}.`,
    description: (data: { filename: string; libraryName: string; isNetworkError?: boolean }) =>
      data.isNetworkError
        ? `Looks like a network hiccup. Check your connection and try again.`
        : `Could not delete ${data.filename} from the library ${data.libraryName}. Please try again. If the problem persists, please contact support.`,
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast', 'notif-bell'],
  },
  'media-delete-success': {
    header: (data: { count: number; libraryName: string }) =>
      `${data.count} media deleted from ${data.libraryName}`,
    description: (data: { storageFreed: string }) =>
      `This operation freed ${data.storageFreed} of storage.`,
    variant: 'success',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast', 'notif-bell'],
    extractDetails: (data: { filenames?: string[] }) =>
      data.filenames && data.filenames.length > 0
        ? { kind: 'media-list', title: 'Deleted media', filenames: data.filenames }
        : undefined,
  },
  'media-delete-error': {
    header: (data: { count: number; libraryName: string }) =>
      `${data.count} media failed to delete from ${data.libraryName}`,
    description: (data: { isNetworkError?: boolean }) =>
      data.isNetworkError
        ? 'Looks like a network hiccup. Check your connection and try again.'
        : 'Please try again. If the problem persists, please contact support.',
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast', 'notif-bell'],
    extractDetails: (data: { filenames?: string[]; onRetry?: () => void | Promise<void> }) =>
      data.filenames && data.filenames.length > 0
        ? {
            kind: 'media-list',
            title: 'Failed deletions',
            filenames: data.filenames,
            ...(data.onRetry ? { onRetryAll: data.onRetry, retryAllLabel: 'Delete all' } : {}),
          }
        : undefined,
  },
  'failed-media-clear-success': {
    header: 'Cleared failed media',
    description: 'All failed media have been removed.',
    variant: 'success',
    duration: 5000,
    surfaces: ['toast', 'notif-bell'],
  },
  'failed-media-clear-error': {
    header: 'Failed to clear failed media',
    description: 'An error occurred while clearing failed media. Please try again.',
    variant: 'error',
    duration: 5000,
    surfaces: ['toast', 'notif-bell'],
  },
  'failed-media-clear-error-unexpected': {
    header: 'Failed to clear failed media',
    description: 'An unexpected error occurred. Please try again.',
    variant: 'error',
    duration: 5000,
    surfaces: ['toast', 'notif-bell'],
  },

  // --- Media (actions): copy link, download, open original ---
  'media-link-copied': {
    header: 'Link copied',
    description: 'Media link copied to clipboard',
    variant: 'success',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  'media-link-copied-at-time': {
    header: 'Link copied',
    description: (data: { seconds: number }) =>
      `Media link copied to clipboard — opens at ${formatClockLabel(data.seconds)}`,
    variant: 'success',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  'media-link-copy-failed': {
    header: 'Failed to copy link',
    description: 'Could not copy link to clipboard',
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'media-original-url-unavailable': {
    header: 'Original URL unavailable',
    description: 'This media was not imported from a URL.',
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'media-original-playback-failed': {
    header: 'Playing the preview instead',
    // Only the format verdict is permanent for this viewer — saying "your browser can't play
    // this" about a dropped connection sends them to install something they don't need.
    description: (data: { reason: OriginalUnavailableReason }) =>
      data.reason === 'unsupported-format'
        ? "Your browser can't play this media's original file. It may use a format only an editing app can open."
        : "Couldn't reach this media's original file. Switching to it again may work if the problem was temporary.",
    variant: 'error',
    duration: 8 * 1000,
    // One switch back can be reported by both the URL resolution and the video element; a
    // stable id keeps that a single toast.
    id: 'media-original-playback-failed',
    surfaces: ['toast'],
  },
  'media-downloading': {
    header: (data: { filename?: string }) =>
      data.filename ? `Downloading ${data.filename}...` : 'Downloading media...',
    description: 'It might take some time to download media. Hang tight!',
    variant: 'success',
    duration: 6 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'media-download-failed': {
    header: 'Failed to download media',
    description: 'Please contact support.',
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast', 'notif-bell'],
  },

  'media-retry-failed': {
    header: 'Retry failed',
    description: (data: { error: string }) =>
      data.error || 'Could not retry processing. Please try again or contact support.',
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast', 'notif-bell'],
  },

  'media-retry-all-result': {
    header: (data: { successes: number; errors: number }) => {
      if (data.errors === 0) {
        return `Retrying ${data.successes === 1 ? '1 media file' : `${data.successes} media files`}...`;
      }
      if (data.successes === 0) {
        return `Failed to retry ${data.errors === 1 ? '1 media file' : `all ${data.errors} media files`}`;
      }
      return `Retried ${data.successes} successfully, ${data.errors} failed`;
    },
    description: (data: { successes: number; errors: number; errorMessages?: string[] }) =>
      data.errors > 0 && data.errorMessages && data.errorMessages.length > 0
        ? data.errorMessages.join(', ')
        : undefined,
    variant: (data: { errors: number }) => (data.errors === 0 ? 'success' : 'error'),
    duration: 5000,
    surfaces: ['toast', 'notif-bell'],
  },

  // --- Media (other): media not ready for modal / indexing ---
  // `media-processing-errored` was removed — MomentModal / BaseMomentModal
  // now push a rolling-window rich-event card instead of firing a toast.
  'media-modal-not-ready': {
    header: 'Media not yet ready.',
    description: (data: { media: Media }) => {
      const media = data.media;
      if (media.status === 'queued' || media.status === 'uploading') {
        return "We are currently uploading the media. Once uploaded and indexed, you'll be able to search for moments in it.";
      }
      return "We are currently indexing the media. You'll be able to view visual summaries, view transcripts, and rapidly search for moments in it once indexing is complete.";
    },
    variant: 'error',
    duration: 6 * 1000,
    suppressIfRecent: true,
    surfaces: ['toast'],
  },

  // --- Library: rename, thumbnail, modify success/error ---
  'library-modify-success': {
    header: 'Library modified.',
    description: (data: {
      name: string;
      oldName: string;
      thumbnailMediaId: string;
      nameChanged: boolean;
      thumbnailChanged: boolean;
      oldMediaTitle: string;
      newMediaTitle: string;
    }) => {
      if (data.nameChanged && data.thumbnailChanged) {
        return (
          <>
            Library name changed from <b>{data.oldName}</b> to <b>{data.name}</b>. Its thumbnail
            also changed, it is now represented by the thumbnail of <b>{data.newMediaTitle}</b>.
          </>
        );
      }
      if (data.nameChanged) {
        return (
          <>
            Library name changed from <b>{data.oldName}</b> to <b>{data.name}</b>.
          </>
        );
      }
      if (data.thumbnailChanged) {
        return (
          <>
            The library <b>{data.name}</b> now uses the thumbnail for <b>{data.newMediaTitle}</b>.
          </>
        );
      }
      return 'Library has been modified.';
    },
    variant: 'success',
    surfaces: ['toast', 'notif-bell'],
  },
  'library-modify-error': {
    header: 'Library modification failed.',
    description: (data: { name: string; thumbnailMediaId: string }) =>
      `Library ${data.name} has not been modified.`,
    variant: 'error',
    surfaces: ['toast', 'notif-bell'],
  },

  // --- Library: delete ---
  'library-delete-success': {
    header: 'Library permanently deleted',
    variant: 'success',
    duration: 3000,
    surfaces: ['toast'],
  },
  'library-delete-failed': {
    header: 'Library deletion failed.',
    description: 'Please contact support if the problem persists.',
    variant: 'error',
    duration: 3000,
    surfaces: ['toast'],
  },
  'library-detach-success': {
    header: 'Library removed',
    variant: 'success',
    duration: 3000,
    surfaces: ['toast'],
  },
  'library-detach-failed': {
    header: "Couldn't remove the library.",
    description: 'Please contact support if the problem persists.',
    variant: 'error',
    duration: 3000,
    surfaces: ['toast'],
  },

  'library-read-only': {
    header: 'This library is read-only.',
    description: 'You cannot add media to read-only libraries.',
    variant: 'error',
    id: 'library-read-only',
    suppressIfRecent: true,
    surfaces: ['toast'],
  },

  // --- Library (selection): selection changed, fallback to demo ---
  'library-no-selection': {
    header: 'No library selected',
    description: 'Switching to demo library',
    variant: 'success',
    duration: 3000,
    surfaces: ['toast'],
  },
  'library-selection-changed': {
    header: 'Library successfully changed',
    variant: 'success',
    duration: 3000,
    surfaces: ['toast'],
  },
  // Distinct from 'library-no-selection' on purpose: there a library *was*
  // selected and the user unselected it, here one was selected and then stopped
  // existing (deleted, or unshared) while it sat in the search scope. "No library
  // selected" would be a confusing thing to read when you never touched the picker.
  'library-unavailable': {
    header: 'That library is no longer available',
    description: 'Switching to the demo library',
    variant: 'error',
    id: 'library-unavailable',
    suppressIfRecent: true,
    duration: 5000,
    surfaces: ['toast'],
  },

  // --- Moments: not found, range, update failed, download progress/complete/failed ---
  'moment-not-found': {
    header: 'Moment not found',
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'moment-range-not-selected': {
    header: 'No moment range selected',
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  'moment-update-failed': {
    header: 'Failed to update moment',
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'moment-downloading': {
    header: (data: { count: number }) => `Downloading ${pluralize(data.count, 'moment')}...`,
    description: 'Please do not close this tab.',
    variant: 'success',
    duration: 3 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'moment-download-failed-individual': {
    header: (data: { index: number }) => `Failed to download moment ${data.index}`,
    description: (data: { error: string }) => `Error: ${data.error}`,
    variant: 'error',
    duration: 3 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'moment-download-complete': {
    header: 'Download Complete',
    description: (data: { count: number }) =>
      `Successfully downloaded ${pluralize(data.count, 'moment')}`,
    variant: 'success',
    duration: 3 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },
  'moment-download-failed': {
    header: 'Download Failed',
    description: (data: { error: string }) => `Failed to download moments: ${data.error}`,
    variant: 'error',
    duration: 3 * 1000,
    surfaces: ['toast', 'notif-bell'],
  },

  // --- Comments: a write on a media's comments failed ---
  // One template for all six writes rather than one each: the reason a write failed is the
  // useful half and it comes from the same set of codes whichever write it was, so only the
  // header changes. `action` names what the reader just pressed.
  'media-comment-failed': {
    header: (data: { action: MediaCommentAction }) => MEDIA_COMMENT_ACTION_HEADERS[data.action],
    description: (data: { error: unknown }) => describeMediaCommentError(data.error),
    variant: 'error',
    duration: 6 * 1000,
    // Toast only. These are failures of something the reader is looking at and about to retry —
    // there is nothing to come back to later, which is what the bell is for.
    surfaces: ['toast'],
  },

  'comment-link-copied': {
    header: 'Link copied',
    description: (data: { label: string }) =>
      `Comment link copied to clipboard — opens on the comment at ${data.label}`,
    variant: 'success',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  // A comment link whose comment is gone. The link still did something — it opened the media at
  // the moment it pointed to (`?t=`) — so this explains the missing highlight rather than
  // reporting a failure.
  'comment-link-not-found': {
    header: 'Comment not found',
    description: 'It may have been deleted. The media is open where it pointed.',
    variant: 'error',
    duration: 8 * 1000,
    surfaces: ['toast'],
  },

  // --- Folders: library folders and filing media in them ---
  // One failure template for every folder write, like 'media-comment-failed': the useful half is
  // why, which comes from the same codes whichever write it was.
  'folder-action-failed': {
    header: (data: { action: FolderAction }) => FOLDER_ACTION_FAILED_HEADERS[data.action],
    description: (data: { error: unknown }) => describeLibraryFolderError(data.error),
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },

  'folder-deleted': {
    header: 'Folder deleted',
    description: (data: {
      name: string;
      mediaCount: number;
      kept?: { count: number; destination: string };
    }) =>
      data.kept && data.kept.count > 0
        ? `“${data.name}” was deleted. Its ${pluralize(data.kept.count, 'media')} moved to “${data.kept.destination}”.`
        : data.mediaCount > 0
          ? `“${data.name}” and the ${pluralize(data.mediaCount, 'media')} in it were deleted.`
          : `“${data.name}” was deleted.`,
    variant: 'success',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },

  // The two move toasts carry "Undo", so they stay long enough to reach it — a drop onto the
  // wrong pill is easy to make.
  'folder-moved': {
    header: 'Folder moved',
    description: (data: { name: string; destination: string }) =>
      `“${data.name}” is now in “${data.destination}”.`,
    variant: 'success',
    duration: 8 * 1000,
    surfaces: ['toast'],
    action: (data: { undo: () => void }) => ({ label: 'Undo', onClick: data.undo }),
  },

  'media-moved-to-folder': {
    header: (data: { count: number }) => `Moved ${pluralize(data.count, 'media')}`,
    description: (data: { destination: string }) => `Now in “${data.destination}”.`,
    variant: 'success',
    duration: 8 * 1000,
    surfaces: ['toast'],
    action: (data: { undo: () => void }) => ({ label: 'Undo', onClick: data.undo }),
  },

  // An "Undo" of a move that drew media from several folders, where some folders took theirs back
  // and some refused. Each folder's media go back all or none, so the rest are where the move put
  // them.
  'media-move-undo-incomplete': {
    header: 'Undo only partly worked',
    description: (data: {
      restored: number;
      stranded: number;
      destination: string;
      error: unknown;
    }) =>
      `${pluralize(data.restored, 'media')} went back, but ${pluralize(data.stranded, 'media')} ${data.stranded === 1 ? 'is' : 'are'} still in “${data.destination}”. ${describeLibraryFolderError(data.error)}`,
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast'],
  },

  // A dropped directory whose folders could not all be made. The files meant for them fail on
  // their own in the upload panel; this says why, once, instead of once per file.
  'folder-create-for-upload-failed': {
    header: (data: { count: number }) =>
      data.count === 1 ? 'A folder couldn’t be created' : 'Some folders couldn’t be created',
    description: (data: { count: number; error: unknown }) =>
      `Media from ${data.count === 1 ? 'that folder' : `${data.count} of the folders you dropped`} wasn’t uploaded. ${describeLibraryFolderError(data.error)}`,
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast', 'notif-bell'],
  },

  // --- Search / image: image drop/paste rejected, file type, search mode, filters ---
  // Copy is a function so it names unified search only while the debug flag
  // makes that mode selectable (see visualSearchModesPhrase).
  'image-drop-rejected': {
    header: 'Image drop rejected.',
    description: () =>
      `Image search is not supported while in conversational search mode. Please switch to ${visualSearchModesPhrase()} mode to search with image queries.`,
    variant: 'error',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'file-drop-rejected': {
    header: 'File drop rejected.',
    description: () =>
      `Only image files are accepted here, and image search requires ${visualSearchModesPhrase()} mode. Please switch modes and drop a supported image file.`,
    variant: 'error',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'image-paste-rejected': {
    header: 'Image paste rejected.',
    description: () =>
      `Image search is not supported while in conversational search mode. Please switch to ${visualSearchModesPhrase()} mode to search with image queries.`,
    variant: 'error',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'file-paste-rejected': {
    header: 'File paste rejected.',
    description: () =>
      `Only image files are accepted here, and image search requires ${visualSearchModesPhrase()} mode. Please switch modes and paste a supported image file.`,
    variant: 'error',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'unsupported-file-type': {
    header: 'Unsupported file type',
    description: `Supported formats for image search queries: ${ACCEPTED_IMAGE_SEARCH_EXTENSIONS.join(', ')}.`,
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  'multiple-images-detected': {
    header: 'Multiple images detected',
    description: 'Image search only supports one image at a time. Using the first image.',
    variant: 'error',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'search-mode-updated': {
    header: 'Search mode updated',
    description: (data: { descriptionType: string }) => {
      const baseMessage = `You're now viewing ${data.descriptionType} results.`;
      return baseMessage;
    },
    variant: 'success',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },

  // --- Billing / credits: credits refresh error ---
  // `upload-insufficient-credits` / `upload-insufficient-storage` were
  // removed — billing failures are now surfaced inline on the BatchCard's
  // 3-CTA BillingActionRow (Top up / View failed / Retry).
  'credits-refresh-error': {
    header: 'Error refreshing credits',
    description:
      'An unexpected error occurred when refreshing your credits. Please wait a few moments then try again. If the error persists, please contact support.',
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast'],
  },

  // --- Billing: subscription checkout could not be opened ---
  // Shown by the paywall and the Pricing page when the backend refuses to start a plan
  // checkout (e.g. a subscription is already attached to the account) or the request fails.
  'subscription-checkout-error': {
    header: "Couldn't start checkout",
    description:
      'Something went wrong opening the checkout. Please try again in a moment. If your account already has a subscription, manage it from Settings → Billing.',
    variant: 'error',
    duration: DEFAULT_TOAST_DURATION,
    surfaces: ['toast'],
  },

  // --- Top-up ---
  'topup-rate-limited': {
    header: 'Too many requests',
    description: 'Please wait a moment before trying again.',
    variant: 'error',
    duration: 6 * 1000,
    id: 'topup-rate-limited',
    surfaces: ['toast'],
  },

  // --- Chats / agents: navigation to the chat app ---
  'chat-open-error': {
    header: "Couldn't open chat",
    description: 'Something went wrong preparing your chat session. Please try again.',
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },

  // --- Account ---
  'nickname-changed': {
    header: 'Nickname changed.',
    description: (data: { nickname: string }) =>
      `Thanks for updating your nickname, ${data.nickname}.`,
    variant: 'success',
    duration: 6 * 1000,
  },
  'nickname-change-failed': {
    header: 'Failed to change nickname',
    description: 'Please try again.',
    variant: 'error',
    duration: 6 * 1000,
  },
  // `set: true` = a magic-link user creating their first password.
  'password-change-success': {
    header: (data: { set?: boolean }) =>
      data?.set ? 'Password set successfully' : 'Password changed successfully',
    variant: 'success',
    duration: 3 * 1000,
  },
  'password-change-failed': {
    header: (data: { set?: boolean }) =>
      data?.set ? 'Failed to set password' : 'Failed to change password',
    variant: 'error',
    duration: 3 * 1000,
  },

  // --- Publishing: social account connections ---
  // One login connects `count` accounts (one on TikTok, every Page shared on Facebook); `names` are those the list knows.
  'social-connect-success': {
    header: (data: SocialConnectSuccessData) =>
      `${data.platformLabel} ${data.accountNoun}${data.count === 1 ? '' : 's'} connected`,
    // Empty string renders no description line.
    description: (data: SocialConnectSuccessData) =>
      data.names.length === 1
        ? `Connected as ${data.names[0]}.`
        : data.names.length > 1
          ? `Connected ${listNames(data.names)}.`
          : data.count > 1
            ? `${data.count} ${data.accountNoun}s connected.`
            : '',
    variant: 'success',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'social-connect-failed': {
    header: (data: SocialConnectFailedData) => `Couldn't connect ${data.platformLabel}`,
    description: (data: SocialConnectFailedData) =>
      describeSocialConnectError(data.errorCode, data.noAccountsNote),
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },
  'social-disconnect-success': {
    header: (data: { platformLabel: string; accountNoun: string }) =>
      `${data.platformLabel} ${data.accountNoun} disconnected`,
    variant: 'success',
    duration: 3 * 1000,
    surfaces: ['toast'],
  },
  'social-disconnect-failed': {
    header: (data: { platformLabel: string; accountNoun: string }) =>
      `Failed to disconnect ${data.platformLabel} ${data.accountNoun}`,
    description: 'Try again.',
    variant: 'error',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },

  // --- Publishing: scheduled posts (compose, edit, cancel, retry) ---
  'social-post-scheduled': {
    header: (data: { count: number; now: boolean }) =>
      data.count === 1 ? 'Post scheduled' : `${data.count} posts scheduled`,
    description: (data: { count: number; now: boolean }) =>
      data.now ? 'Publishing starts in a moment.' : '',
    variant: 'success',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'social-post-saved': {
    header: 'Post updated',
    variant: 'success',
    duration: 3 * 1000,
    surfaces: ['toast'],
  },
  'social-post-cancelled': {
    header: 'Post cancelled',
    description: 'It stays in your schedule as a record.',
    variant: 'success',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  'social-post-retried': {
    header: 'Retrying the post',
    description: 'It is back in the schedule and publishes shortly.',
    variant: 'success',
    duration: 4 * 1000,
    surfaces: ['toast'],
  },
  // The post's status moved under the user (the worker claimed it, or another tab acted). The page reloads it.
  'social-post-status-changed': {
    header: (data: { statusLabel: string }) => `This post is now ${data.statusLabel.toLowerCase()}`,
    description: 'It can no longer be changed from here.',
    variant: 'error',
    duration: 5 * 1000,
    surfaces: ['toast'],
  },
  'social-post-action-failed': {
    header: (data: { action: 'schedule' | 'save' | 'cancel' | 'retry'; description: string }) =>
      ({
        schedule: 'Couldn’t schedule the post',
        save: 'Couldn’t save the post',
        cancel: 'Couldn’t cancel the post',
        retry: 'Couldn’t retry the post',
      })[data.action],
    description: (data: { action: string; description: string }) => data.description,
    variant: 'error',
    duration: 6 * 1000,
    surfaces: ['toast'],
  },

  // --- App: part of the page failed to render (error boundary caught a crash) ---
  // Fired from `SubtreeErrorBoundary`.
  //
  // The stable `id` is what does the de-duplication work: two boundaries catching in
  // the same commit, or a user hammering "Try again", collapse into one toast because
  // sonner replaces by id instead of stacking. `suppressIfRecent` is set for when it
  // becomes reachable again, but note it is currently INERT for everyone — the
  // suppression branch in `showToast` is gated on `!debugStore.showUploadStatus`, and
  // `showUploadStatus` is force-enabled (`FORCE_UPLOAD_STATUS_ENABLED`). Don't rely on
  // it here.
  //
  // Deliberately vague about which part broke — the user can see the gap on the page,
  // and naming internal components at them helps nobody.
  'section-render-failed': {
    header: 'Something went wrong',
    description: "Part of this page couldn't be displayed. Reloading usually fixes it.",
    variant: 'error',
    duration: 8 * 1000,
    id: 'section-render-failed',
    suppressIfRecent: true,
    surfaces: ['toast'],
  },
};

/**
 * Show a toast notification using a predefined template
 * @param templateId - The slug identifying the toast template
 * @param data - Optional data to fill in template placeholders. Can include an 'error' field for logging.
 */
export function showToast(templateId: string, data?: any): void {
  const template = toastTemplates[templateId];

  if (!template) {
    console.error(`Toast template not found: ${templateId}`);
    return;
  }

  // Check if this toast should be suppressed due to recent duplicates
  // (only applies when showing visual toasts, not when routing to upload status panel)
  const lastShownAt = recentToasts.get(templateId);
  if (
    !debugStore.showUploadStatus &&
    template.suppressIfRecent &&
    lastShownAt &&
    Date.now() - lastShownAt < (template.duration ?? DEFAULT_TOAST_DURATION)
  ) {
    // Instead of silently suppressing, update the toast with a new shakeKey to trigger shake animation
    const existingToastId = activeToastIds.get(templateId);
    if (existingToastId !== undefined) {
      // Re-evaluate template with current data so the toast reflects the latest state
      const header =
        typeof template.header === 'function' ? template.header(data) : template.header;
      const description =
        typeof template.description === 'function'
          ? template.description(data)
          : template.description;
      const variant =
        typeof template.variant === 'function' ? template.variant(data) : template.variant;

      toast.custom(
        (t) => (
          <Toast
            id={t}
            header={header}
            description={description}
            variant={variant}
            shakeKey={Date.now()}
            action={template.action?.(data)}
          />
        ),
        {
          id: existingToastId,
          duration: template.duration,
        },
      );
      recentToasts.set(templateId, Date.now());
      return;
    }
  }

  // If error is provided in data, log it to console
  if (data?.error) {
    console.error(`[${templateId}]`, data.error);
  }

  const header = typeof template.header === 'function' ? template.header(data) : template.header;
  const description =
    typeof template.description === 'function' ? template.description(data) : template.description;
  const variant =
    typeof template.variant === 'function' ? template.variant(data) : template.variant;
  const duration = template.duration;
  const toastId =
    template.id != null
      ? typeof template.id === 'function'
        ? template.id(data)
        : template.id
      : undefined;

  trackingStore.toastShown({ templateId, variant });

  // Track this toast in recent history
  recentToasts.set(templateId, Date.now());

  const surfaces = template.surfaces ?? DEFAULT_SURFACES;

  // Visual sonner toast. Skipped only when the template explicitly opts out
  // of the `'toast'` surface — defaults are unchanged from pre-routing
  // behaviour.
  const actualToastId: string | number | null = surfaces.includes('toast')
    ? toast.custom(
        (t) =>
          template.customRender ? (
            template.customRender(t, data)
          ) : (
            <Toast
              id={t}
              header={header}
              description={description}
              variant={variant}
              templateId={templateId}
              action={template.action?.(data)}
            />
          ),
        {
          duration,
          ...(toastId != null && { id: toastId }),
        },
      )
    : null;

  // Store the toast ID if this template uses suppressIfRecent. Only
  // tracked when a visual toast actually rendered — bell-only templates
  // never use the suppress-if-recent path.
  if (template.suppressIfRecent && actualToastId != null) {
    activeToastIds.set(templateId, actualToastId);
  }

  // String-coerce the rich (JSX) payload for the bell mirrors. Templates
  // whose description is JSX lose their inline `<b>filename</b>` formatting
  // here — acceptable trade-off for now since (a) the ephemeral toast still
  // carries the rich version and (b) the bell cards historically dropped
  // descriptions altogether on JSX. Future templates that want full bell
  // fidelity should keep their description as a plain string.
  const headerStr = typeof header === 'string' ? header : templateId;
  const descStr = typeof description === 'string' ? description : undefined;

  // Mirror into the upload-activity panel. Gated on the debug flag (the
  // panel is still behind it) and on the template's surfaces list.
  if (debugStore.showUploadStatus && surfaces.includes('upload-activity')) {
    notificationStore.add({
      type: 'generic-toast',
      header: headerStr,
      description: descStr,
      variant,
      toastId: actualToastId ?? undefined,
    });
  }

  // Mirror into the new notif-bell. No debug flag — the bell is meant to
  // be GA from the moment it ships.
  if (surfaces.includes('notif-bell')) {
    const details = template.extractDetails?.(data);
    notificationStore.add({
      type: 'notif-bell-entry',
      header: headerStr,
      description: descStr,
      variant,
      toastId: actualToastId ?? undefined,
      ...(details ? { details } : {}),
    });
  }
}
